/**
 * Designed  & Developed by Faisal Nadaf
 * Contact: faisalfnadaf@gmail.com
 *
 * @format
 */

import React, { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
	PageHeader,
	RevealOnScroll,
} from "@/pages/About/SocialInitiatives.page-primitives";

gsap.registerPlugin(ScrollTrigger);

// ---------------- TYPES ----------------
interface Initiative {
	name: string;
	image: string;
	description: string;
}

interface Section {
	title: string;
	initiatives: Initiative[];
	description?: string;
}

interface InitiativesProps {
	data: {
		title: string;
		description: string;
		sections: {
			environmental_management: Section;
			social_engagement: Section;
		};
	};
}

// ---------------- CARD ----------------
const InitiativeCard: React.FC<{
	item: Initiative;
	large?: boolean;
	index?: number;
}> = ({ item, large, index = 0 }) => {
	const [isHovered, setIsHovered] = useState(false);

	return (
		<motion.div
			initial={{ opacity: 0, scale: 0.95 }}
			whileInView={{ opacity: 1, scale: 1 }}
			viewport={{ once: true, margin: "-100px" }}
			transition={{ delay: index * 0.1, duration: 0.5 }}
			whileHover={{ scale: 1.02 }}
			onHoverStart={() => setIsHovered(true)}
			onHoverEnd={() => setIsHovered(false)}
			className={`relative overflow-hidden group rounded-2xl ${
				large ? "h-[75vh]" : "h-[440px] shadow-md"
			}`}>
			{/* IMAGE with parallax effect */}
			<motion.img
				src={item.image}
				className="absolute inset-0 w-full h-full object-cover"
				whileHover={{ scale: 1.1 }}
				transition={{ duration: 0.4 }}
			/>

			{/* OVERLAY - changes on hover */}
			<motion.div
				className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent"
				animate={{
					background:
						isHovered ?
							"linear-gradient(to top, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.7) 50%, rgba(0,0,0,0.2) 100%)"
						:	"linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.4) 60%, rgba(0,0,0,0) 100%)",
				}}
				transition={{ duration: 0.3 }}
			/>

			{/* CONTENT - always visible, expands on hover */}
			<motion.div
				className="absolute bottom-0 p-8 text-white w-full"
				animate={{
					y: isHovered ? 0 : 20,
					paddingBottom: isHovered ? "2rem" : "1.5rem",
				}}
				transition={{ duration: 0.3 }}>
				{/* Title */}
				<motion.h3
					className="text-2xl md:text-3xl font-bold mt-2 mb-5"
					animate={{
						y: isHovered ? 0 : 5,
					}}
					transition={{ duration: 0.3 }}>
					{item.name}
				</motion.h3>

				{/* Description - appears on hover */}
				<motion.div
					initial={false}
					animate={{
						height: isHovered ? "auto" : 0,
						opacity: isHovered ? 1 : 0,
						marginTop: isHovered ? "1rem" : 0,
					}}
					transition={{ duration: 0.3, ease: "easeOut" }}
					className="overflow-hidden">
					<p className="text-sm text-gray-200 leading-relaxed">
						{item.description}
					</p>
				</motion.div>
			</motion.div>
		</motion.div>
	);
};

// ---------------- INFINITE FULL-SCREEN SLIDER ----------------
const SocialSlider: React.FC<{ items: Initiative[] }> = ({ items }) => {
	const { t } = useTranslation();
	const [currentIndex, setCurrentIndex] = useState(0);
	const [isDragging, setIsDragging] = useState(false);
	const [dragStart, setDragStart] = useState(0);
	const sliderRef = useRef<HTMLDivElement>(null);
	const autoPlayRef = useRef<NodeJS.Timeout | null>(null);
	const [itemsPerView, setItemsPerView] = useState(2.5); // Wider cards - show 2.5 items

	// Create extended array for infinite effect (duplicate items)
	const extendedItems = [...items, ...items, ...items];

	// Calculate items per view based on screen size (wider cards)
	useEffect(() => {
		const updateItemsPerView = () => {
			if (window.innerWidth < 768)
				setItemsPerView(1.2); // Show 1.2 cards on mobile
			else if (window.innerWidth < 1024)
				setItemsPerView(1.8); // Show 1.8 cards on tablet
			else setItemsPerView(2.5); // Show 2.5 cards on desktop (wider)
		};

		updateItemsPerView();
		window.addEventListener("resize", updateItemsPerView);
		return () => window.removeEventListener("resize", updateItemsPerView);
	}, []);

	const slideWidth = 100 / itemsPerView;
	const visibleSlides = Math.floor(itemsPerView);

	const goToSlide = useCallback(
		(index: number) => {
			let newIndex = index;

			// Handle infinite loop boundaries
			if (newIndex >= items.length + items.length) {
				newIndex = items.length;
				setCurrentIndex(items.length);
				setTimeout(() => {
					if (sliderRef.current) {
						sliderRef.current.style.transition = "none";
						const offset = -(items.length * slideWidth);
						sliderRef.current.style.transform = `translateX(${offset}%)`;
						setCurrentIndex(items.length);
						setTimeout(() => {
							if (sliderRef.current) {
								sliderRef.current.style.transition =
									"transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)";
							}
						}, 50);
					}
				}, 0);
				return;
			}

			if (newIndex < items.length) {
				newIndex = items.length + items.length - Math.ceil(visibleSlides);
				setCurrentIndex(newIndex);
				setTimeout(() => {
					if (sliderRef.current) {
						sliderRef.current.style.transition = "none";
						const offset = -(newIndex * slideWidth);
						sliderRef.current.style.transform = `translateX(${offset}%)`;
						setCurrentIndex(newIndex);
						setTimeout(() => {
							if (sliderRef.current) {
								sliderRef.current.style.transition =
									"transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)";
							}
						}, 50);
					}
				}, 0);
				return;
			}

			setCurrentIndex(newIndex);
		},
		[items.length, slideWidth, visibleSlides],
	);

	const next = useCallback(() => {
		goToSlide(currentIndex + 1);
	}, [currentIndex, goToSlide]);

	const prev = useCallback(() => {
		goToSlide(currentIndex - 1);
	}, [currentIndex, goToSlide]);

	// Auto-play functionality
	useEffect(() => {
		autoPlayRef.current = setInterval(() => {
			if (!isDragging) next();
		}, 5000);

		return () => {
			if (autoPlayRef.current) clearInterval(autoPlayRef.current);
		};
	}, [next, isDragging]);

	// Drag handlers for touch/mouse
	const handleDragStart = (e: React.MouseEvent | React.TouchEvent) => {
		setIsDragging(true);
		const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
		setDragStart(clientX);
		if (autoPlayRef.current) clearInterval(autoPlayRef.current);
	};

	const handleDragEnd = (e: React.MouseEvent | React.TouchEvent) => {
		if (!isDragging) return;
		setIsDragging(false);

		const clientX =
			"changedTouches" in e ? e.changedTouches[0].clientX : e.clientX;
		const dragDistance = clientX - dragStart;

		if (Math.abs(dragDistance) > 50) {
			if (dragDistance > 0) prev();
			else next();
		}

		// Restart auto-play
		autoPlayRef.current = setInterval(() => {
			if (!isDragging) next();
		}, 5000);
	};

	const handleDragMove = (e: React.MouseEvent | React.TouchEvent) => {
		if (!isDragging) return;
		e.preventDefault();
		const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
		const dragDelta = clientX - dragStart;
		const currentOffset = -(currentIndex * slideWidth);
		const newOffset =
			currentOffset +
			(dragDelta / sliderRef.current!.parentElement!.clientWidth) * 100;

		if (sliderRef.current) {
			sliderRef.current.style.transform = `translateX(${newOffset}%)`;
		}
	};

	return (
		<div className="w-full relative">
			{/* FULL-SCREEN SLIDER CONTAINER - Removed px margins */}
			<div
				className="w-full overflow-hidden cursor-grab active:cursor-grabbing"
				onMouseDown={handleDragStart}
				onMouseUp={handleDragEnd}
				onMouseMove={handleDragMove}
				onMouseLeave={handleDragEnd}
				onTouchStart={handleDragStart}
				onTouchEnd={handleDragEnd}
				onTouchMove={handleDragMove}>
				<div
					ref={sliderRef}
					className="flex transition-transform duration-600 ease-out"
					style={{ transform: `translateX(-${currentIndex * slideWidth}%)` }}>
					{extendedItems.map((item, i) => (
						<div
							key={`${item.name}-${i}`}
							className="flex-shrink-0 px-2 first:pl-0 last:pr-0"
							style={{ width: `${slideWidth}%` }}>
							<InitiativeCard
								item={item}
								large
								index={i % items.length}
							/>
						</div>
					))}
				</div>
			</div>

			{/* NAV BUTTONS - Positioned absolutely over the slider */}
			<div className="absolute inset-y-0 left-0 right-0 flex items-center justify-between px-4 pointer-events-none">
				<button
					onClick={prev}
					className="pointer-events-auto bg-black/50 backdrop-blur-md p-4 rounded-full shadow-lg hover:scale-110 transition-all duration-300 hover:bg-black/70 text-white z-20"
					aria-label={t("common.ui.previousSlide")}>
					<svg
						className="w-6 h-6"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24">
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={2}
							d="M15 19l-7-7 7-7"
						/>
					</svg>
				</button>

				<button
					onClick={next}
					className="pointer-events-auto bg-black/50 backdrop-blur-md p-4 rounded-full shadow-lg hover:scale-110 transition-all duration-300 hover:bg-black/70 text-white z-20"
					aria-label={t("common.ui.nextSlide")}>
					<svg
						className="w-6 h-6"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24">
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={2}
							d="M9 5l7 7-7 7"
						/>
					</svg>
				</button>
			</div>

			{/* PROGRESS BAR */}
			<div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 w-64 h-1 bg-black rounded-full overflow-hidden z-10 border">
				<motion.div
					className="h-full bg-white rounded-full"
					animate={{
						width: `${((currentIndex % items.length) / items.length) * 100}%`,
					}}
					transition={{ duration: 0.3 }}
				/>
			</div>
		</div>
	);
};

// ---------------- SECTION ----------------
const InitiativeSection: React.FC<{
	section: Section;
	isCarousel?: boolean;
}> = ({ section, isCarousel = false }) => {
	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!ref.current) return;

		const tl = gsap.fromTo(
			ref.current,
			{ opacity: 0, y: 60 },
			{
				opacity: 1,
				y: 0,
				duration: 0.8,
				ease: "power3.out",
				scrollTrigger: {
					trigger: ref.current,
					start: "top 85%",
					toggleActions: "play none none reverse",
				},
			},
		);

		return () => {
			tl.kill();
			ScrollTrigger.getAll().forEach((trigger) => {
				if (trigger.vars.trigger === ref.current) trigger.kill();
			});
		};
	}, []);

	return (
		<div
			ref={ref}
			className="mb-20 last:mb-0">
			<RevealOnScroll>
				<PageHeader
					title={section.title}
					description={section.description}
					align="center"
					className="py-14 text-center items-center "
				/>
			</RevealOnScroll>

			{/* CONTENT - Full width for carousel */}
			{isCarousel ?
				<div className="w-full">
					<SocialSlider items={section.initiatives} />
				</div>
			:	<div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-7xl mx-auto px-6">
					{section.initiatives.map((item, i) => (
						<InitiativeCard
							key={i}
							item={item}
							index={i}
						/>
					))}
				</div>
			}
		</div>
	);
};

const InitiativeSectionSocialEngagement: React.FC<{
	section: Section;
}> = ({ section }) => {
	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!ref.current) return;

		const tl = gsap.fromTo(
			ref.current,
			{ opacity: 0, y: 60 },
			{
				opacity: 1,
				y: 0,
				duration: 0.8,
				ease: "power3.out",
				scrollTrigger: {
					trigger: ref.current,
					start: "top 85%",
				},
			},
		);

		return () => {
			tl.kill();
			ScrollTrigger.getAll().forEach((trigger) => {
				if (trigger.vars.trigger === ref.current) trigger.kill();
			});
		};
	}, []);

	return (
		<div
			ref={ref}
			className="mb-20 last:mb-0">
			<RevealOnScroll>
				<PageHeader
					title={section.title}
					description={section.description}
					align="center"
					className="py-14 text-center"
				/>
			</RevealOnScroll>

			{/* GRID - 3x3 */}
			<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto px-6">
				{section.initiatives.map((item, i) => (
					<InitiativeCardSocialEngagement
						key={i}
						item={item}
						index={i}
					/>
				))}
			</div>
		</div>
	);
};

const InitiativeCardSocialEngagement: React.FC<{
	item: Initiative;
	index?: number;
}> = ({ item, index = 0 }) => {
	return (
		<motion.div
			initial={{ opacity: 0, y: 30 }}
			whileInView={{ opacity: 1, y: 0 }}
			viewport={{ once: true }}
			transition={{ delay: index * 0.08, duration: 0.5 }}
			className="relative overflow-hidden rounded-2xl h-[360px] shadow-md">
			{/* IMAGE */}
			<img
				src={item.image}
				className="absolute inset-0 w-full h-full object-cover"
				alt={item.name}
			/>

			{/* OVERLAY */}
			<div className="absolute inset-0 bg-black/70" />

			{/* CONTENT */}
			<div className="absolute inset-0 p-6 flex flex-col justify-end text-white">
				<h3 className="text-xl md:text-2xl font-semibold mb-2">{item.name}</h3>
				<p className="text-sm text-gray-200 leading-relaxed line-clamp-5">
					{item.description}
				</p>
			</div>
		</motion.div>
	);
};

// ---------------- MAIN ----------------
const SocialEnvironmentalInitiatives: React.FC<InitiativesProps> = ({
	data,
}) => {
	return (
		<section className="bg-gray-50 py-20 overflow-hidden">
			<div
				className={`
				${data.sections.social_engagement.initiatives.length > 0 ? "w-full" : "max-w-7xl mx-auto"}
			`}>
				{/* <RevealOnScroll className="w-9xl">
					<PageHeader
						title={data.title}
						description={data.description}
						align="center"
						className="py-14 text-center w-7xl items-center "
					/>
				</RevealOnScroll> */}

				{/* ENVIRONMENTAL */}
				<InitiativeSection section={data.sections.environmental_management} />

				{/* SOCIAL (SLIDER) - Full width */}
				<InitiativeSectionSocialEngagement
					section={data.sections.social_engagement}
				/>
			</div>
		</section>
	);
};

export default SocialEnvironmentalInitiatives;
